import {
  Bell,
  AlertTriangle,
  Clock,
  XCircle,
  Info,
  ArrowRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover.tsx";
import { StatusBadge } from "./StatusBadge.tsx";
import { cn } from "../../lib/utils.ts";

const alertIcons = {
  info: Info,
  warning: Clock,
  critical: AlertTriangle,
  expired: XCircle,
};

export function AlertsDropdown({ alerts, unreadCount, onAlertClick }) {
  const navigate = useNavigate();

  const handleAlertClick = (alert) => {
    if (onAlertClick) {
      onAlertClick(alert);
    }
  };

  const handleViewAll = () => {
    navigate("/alertas");
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[20px] h-5 px-1 text-xs font-bold rounded-full bg-red-500 text-white animate-pulse">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-96 p-0" align="end">
        {/* Header */}
        <div className="p-4 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
          <h3 className="font-semibold text-lg flex items-center">
            <Bell className="h-5 w-5 mr-2" />
            Alertas Recientes
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {unreadCount}{" "}
            {unreadCount === 1 ? "alerta pendiente" : "alertas pendientes"}
          </p>
        </div>

        {/* Lista de alertas (primeras 10) */}
        <div className="max-h-[400px] overflow-y-auto">
          {alerts.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Bell className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm font-medium">No hay alertas</p>
              <p className="text-xs mt-1">Estás al día con tus documentos</p>
            </div>
          ) : (
            <div className="divide-y">
              {alerts.slice(0, 10).map((alert) => {
                const Icon = alertIcons[alert.alert_level || alert.level];
                return (
                  <div
                    key={alert.id}
                    role="button"
                    tabIndex={0}
                    className={cn(
                      "p-3 hover:bg-gray-50 transition-colors cursor-pointer",
                      !alert.is_read && "bg-blue-50/30",
                    )}
                    onClick={() => handleAlertClick(alert)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleAlertClick(alert);
                      }
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={cn(
                          "p-2 rounded-full mt-0.5 flex-shrink-0",
                          (alert.alert_level === "expired" ||
                            alert.level === "expired") &&
                            "bg-red-100 text-red-600",
                          (alert.alert_level === "critical" ||
                            alert.level === "critical") &&
                            "bg-orange-100 text-orange-600",
                          (alert.alert_level === "warning" ||
                            alert.level === "warning") &&
                            "bg-yellow-100 text-yellow-600",
                          (alert.alert_level === "info" ||
                            alert.level === "info") &&
                            "bg-blue-100 text-blue-600",
                        )}
                      >
                        <Icon className="h-4 w-4" />
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 leading-snug">
                          {alert.message}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <StatusBadge
                            status={alert.alert_level || alert.level}
                            size="sm"
                          />
                          <span className="text-xs text-gray-500">
                            {new Date(alert.alert_date).toLocaleDateString(
                              "es-PE",
                            )}
                          </span>
                        </div>
                      </div>

                      {!alert.is_read && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer con botón "Ver todas" */}
        {alerts.length > 0 && (
          <div className="p-3 border-t bg-gray-50">
            <button
              onClick={handleViewAll}
              className="w-full px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 font-medium transition-all shadow-sm hover:shadow-md flex items-center justify-center gap-2"
            >
              <span>Ver todas las alertas</span>
              {alerts.length > 10 && (
                <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs">
                  {alerts.length}
                </span>
              )}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
